import { Link, useNavigate, useLocation } from 'react-router';
import { User, Menu as MenuIcon, ChevronDown, X } from 'lucide-react';
import { useState, Fragment } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { useCartStore } from '@/stores/useCartStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useGuestOrdersStore } from '@/stores/useGuestOrdersStore';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { CartBadge } from '@/features/cart/components/CartBadge';
import { CartDrawer } from '@/features/cart/components/CartDrawer';
import { AddedToCartBanner } from '@/features/cart/components/AddedToCartBanner';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';

export const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { items, openDrawer } = useCartStore();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const { isAuthenticated, user } = useAuthStore();
    const hasGuestOrders = useGuestOrdersStore((s) => s.orders.length > 0);
    const { logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isActive = (path: string, exact = false) =>
        exact ? pathname === path : pathname === path || pathname.startsWith(path + '/');

    const desktopNavClass = (path: string, exact = false) =>
        cn(
            'text-sm font-semibold leading-6 transition-colors',
            isActive(path, exact)
                ? 'text-accent-on-dark underline decoration-accent-on-dark decoration-2 underline-offset-4'
                : 'text-white/75 hover:text-white'
        );

    const mobileNavClass = (path: string, exact = false) =>
        cn(
            'block rounded-md px-3 py-2 text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
            isActive(path, exact)
                ? 'bg-muted text-primary'
                : 'text-foreground hover:bg-muted hover:text-primary'
        );

    const handleLogout = () => {
        logout.mutate(undefined, {
            onSuccess: () => {
                navigate('/');
                setIsMobileMenuOpen(false);
            }
        });
    };

    const handleCartClick = () => {
        openDrawer();
    };

    return (
        <>
            <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-footer/95 backdrop-blur-md" role="banner">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                    {/* Logo */}
                    <div className="flex lg:flex-1 items-center gap-8">
                        <Link to="/" className="-m-1.5 p-1.5" aria-label="Stork Rentals - Go to homepage">
                            <img
                                src="/assets/images/logo/TSELogoFull.png"
                                alt="Stork Rentals"
                                className="h-10 w-auto hidden sm:block"
                                width="158"
                                height="40"
                            />
                            <img
                                src="/assets/images/logo/TSELogo.png"
                                alt="Stork Rentals"
                                className="h-10 w-auto sm:hidden"
                                width="40"
                                height="40"
                            />
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex lg:gap-x-12 items-center" aria-label="Main navigation">
                        <Link
                            to="/"
                            className={desktopNavClass('/', true)}
                            aria-current={isActive('/', true) ? 'page' : undefined}
                        >
                            Home
                        </Link>
                        <Link
                            to="/catalogue"
                            className={desktopNavClass('/catalogue')}
                            aria-current={isActive('/catalogue') ? 'page' : undefined}
                        >
                            Catalogue
                        </Link>
                        <Link
                            to="/locations"
                            className={desktopNavClass('/locations')}
                            aria-current={isActive('/locations') ? 'page' : undefined}
                        >
                            Locations
                        </Link>

                        {/* About Us Dropdown */}
                        <Menu as="div" className="relative inline-block text-left">
                            <Menu.Button className={cn(
                                'flex items-center gap-x-1 text-sm font-semibold leading-6 outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-footer rounded-sm transition-colors',
                                isActive('/about') || isActive('/partners')
                                    ? 'text-accent-on-dark underline decoration-accent-on-dark decoration-2 underline-offset-4'
                                    : 'text-white/75 hover:text-white'
                            )}>
                                About Us
                                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            </Menu.Button>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-border focus:outline-none">
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/about"
                                                    className={cn(
                                                        active ? 'bg-muted text-primary' : 'text-foreground',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Our Ethos
                                                </Link>
                                            )}
                                        </Menu.Item>
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    to="/partners"
                                                    className={cn(
                                                        active ? 'bg-muted text-primary' : 'text-foreground',
                                                        'block px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    Our Partners
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>

                        <Link
                            to="/contact"
                            className={desktopNavClass('/contact')}
                            aria-current={isActive('/contact') ? 'page' : undefined}
                        >
                            Contact
                        </Link>

                        {/* Account link for authenticated users */}
                        {isAuthenticated && (
                            <Link
                                to="/account"
                                className={desktopNavClass('/account')}
                                aria-current={isActive('/account') ? 'page' : undefined}
                            >
                                My Account
                            </Link>
                        )}

                        {/* Saved guest orders (this device) — shown only when present */}
                        {hasGuestOrders && (
                            <Link
                                to="/orders"
                                className={desktopNavClass('/orders')}
                                aria-current={isActive('/orders') ? 'page' : undefined}
                            >
                                Your orders
                            </Link>
                        )}
                    </nav>

                    {/* Right Side Actions */}
                    <div className="flex flex-1 items-center justify-end gap-x-4">
                        {/* Currency Switcher */}
                        <LocaleSwitcher />
                        <CurrencySwitcher variant="compact-dark" className="hidden sm:block" />

                        {/* Cart - Click opens drawer, right-click/long-press goes to page */}
                        <CartBadge onClick={handleCartClick} className="text-white/75 hover:text-white" />

                        {/* User Menu (Desktop) */}
                        <div className="hidden lg:flex lg:items-center lg:gap-x-4 lg:border-l lg:border-white/20 lg:pl-4">
                            {isAuthenticated ? (
                                <Menu as="div" className="relative inline-block text-left">
                                    <Menu.Button className="-m-2 flex items-center p-2 text-white/75 hover:text-white outline-none">
                                        <span className="sr-only">Open user menu</span>
                                        <User className="h-6 w-6" aria-hidden="true" />
                                        <span className="ml-2 text-sm font-medium text-white/90">{user?.firstName}</span>
                                    </Menu.Button>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-border focus:outline-none">
                                            <div className="py-1">
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/account"
                                                            className={cn(
                                                                active ? 'bg-muted text-primary' : 'text-foreground',
                                                                'block px-4 py-2 text-sm'
                                                            )}
                                                        >
                                                            My Account
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/reservations"
                                                            className={cn(
                                                                active ? 'bg-muted text-primary' : 'text-foreground',
                                                                'block px-4 py-2 text-sm'
                                                            )}
                                                        >
                                                            My Reservations
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/monthly-rentals"
                                                            className={cn(
                                                                active ? 'bg-muted text-primary' : 'text-foreground',
                                                                'block px-4 py-2 text-sm'
                                                            )}
                                                        >
                                                            My Monthly Rentals
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/profile"
                                                            className={cn(
                                                                active ? 'bg-muted text-primary' : 'text-foreground',
                                                                'block px-4 py-2 text-sm'
                                                            )}
                                                        >
                                                            My Profile
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            onClick={handleLogout}
                                                            className={cn(
                                                                active ? 'bg-muted text-destructive' : 'text-destructive',
                                                                'block w-full text-left px-4 py-2 text-sm'
                                                            )}
                                                        >
                                                            Sign out
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </div>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                            ) : (
                                <Link to="/login" className="-m-2 flex items-center p-2 text-white/75 hover:text-white transition-colors">
                                    <span className="sr-only">Sign in</span>
                                    <User className="h-6 w-6" aria-hidden="true" />
                                </Link>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="flex lg:hidden">
                            <button
                                type="button"
                                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                onClick={() => setIsMobileMenuOpen(true)}
                                aria-expanded={isMobileMenuOpen ? "true" : "false"}
                                aria-controls="mobile-menu"
                            >
                                <span className="sr-only">Open main menu</span>
                                <MenuIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu - Dialog for proper focus trapping */}
            <Transition.Root show={isMobileMenuOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setIsMobileMenuOpen}>
                    {/* Backdrop */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
                    </Transition.Child>

                    {/* Menu Panel */}
                    <div className="fixed inset-0 z-50 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transform transition ease-in-out duration-300"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transform transition ease-in-out duration-300"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel
                                id="mobile-menu"
                                className="relative mr-16 flex w-full max-w-xs flex-1 flex-col bg-white"
                            >
                                {/* Close button */}
                                <div className="absolute top-0 right-0 -mr-12 pt-4">
                                    <button
                                        type="button"
                                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-white"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <span className="sr-only">Close menu</span>
                                        <X className="h-6 w-6 text-white" aria-hidden="true" />
                                    </button>
                                </div>

                                {/* Menu header */}
                                <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                                    <Dialog.Title className="text-lg font-semibold text-foreground">
                                        Menu
                                    </Dialog.Title>
                                </div>

                                {/* Navigation Links */}
                                <nav className="flex-1 overflow-y-auto" aria-label="Mobile navigation">
                                    <div className="space-y-1 px-2 py-3">
                                        <Link
                                            to="/"
                                            className={mobileNavClass('/', true)}
                                            aria-current={isActive('/', true) ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Home
                                        </Link>
                                        <Link
                                            to="/catalogue"
                                            className={mobileNavClass('/catalogue')}
                                            aria-current={isActive('/catalogue') ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Catalogue
                                        </Link>
                                        <Link
                                            to="/locations"
                                            className={mobileNavClass('/locations')}
                                            aria-current={isActive('/locations') ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Locations
                                        </Link>

                                        {/* About Us section */}
                                        <p className="px-3 py-2 text-base font-medium text-foreground">
                                            About Us
                                        </p>
                                        <Link
                                            to="/about"
                                            className={cn(mobileNavClass('/about'), 'pl-6')}
                                            aria-current={isActive('/about') ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Our Ethos
                                        </Link>
                                        <Link
                                            to="/partners"
                                            className={cn(mobileNavClass('/partners'), 'pl-6')}
                                            aria-current={isActive('/partners') ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Our Partners
                                        </Link>
                                        <Link
                                            to="/cart"
                                            className={mobileNavClass('/cart')}
                                            aria-current={isActive('/cart') ? 'page' : undefined}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            Cart ({itemCount})
                                        </Link>

                                        {/* Saved guest orders (this device) — shown only when present */}
                                        {hasGuestOrders && (
                                            <Link
                                                to="/orders"
                                                className={mobileNavClass('/orders')}
                                                aria-current={isActive('/orders') ? 'page' : undefined}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                Your orders
                                            </Link>
                                        )}

                                        {/* Currency Switcher for Mobile */}
                                        <div className="px-3 py-2">
                                            <span className="block text-base font-medium text-foreground mb-2">Currency</span>
                                            <CurrencySwitcher variant="default" />
                                        </div>

                                        <Link to="/contact" className={mobileNavClass('/contact')} aria-current={isActive('/contact') ? 'page' : undefined} onClick={() => setIsMobileMenuOpen(false)}>
                                            Contact
                                        </Link>
                                        {isAuthenticated ? (
                                            <>
                                                <Link
                                                    to="/account"
                                                    className={mobileNavClass('/account')}
                                                    aria-current={isActive('/account') ? 'page' : undefined}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    My Account
                                                </Link>
                                                <Link
                                                    to="/reservations"
                                                    className={mobileNavClass('/reservations')}
                                                    aria-current={isActive('/reservations') ? 'page' : undefined}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    My Reservations
                                                </Link>
                                                <Link
                                                    to="/monthly-rentals"
                                                    className={mobileNavClass('/monthly-rentals')}
                                                    aria-current={isActive('/monthly-rentals') ? 'page' : undefined}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    My Monthly Rentals
                                                </Link>
                                                <Link
                                                    to="/profile"
                                                    className={mobileNavClass('/profile')}
                                                    aria-current={isActive('/profile') ? 'page' : undefined}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                >
                                                    My Profile
                                                </Link>
                                                <button
                                                    onClick={handleLogout}
                                                    className="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-destructive hover:bg-destructive-subtle focus:outline-none focus:ring-2 focus:ring-destructive"
                                                >
                                                    Sign out
                                                </button>
                                            </>
                                        ) : (
                                            <Link
                                                to="/login"
                                                className={mobileNavClass('/login')}
                                                aria-current={isActive('/login') ? 'page' : undefined}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                Sign in
                                            </Link>
                                        )}
                                    </div>
                                </nav>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Cart Drawer */}
            <CartDrawer />

            {/* Mobile-only: lightweight snackbar shown instead of opening the drawer on add */}
            <AddedToCartBanner />
        </>
    );
};
